import { getMint } from '@solana/spl-token'
import { TOKEN_PROGRAM_ID } from '@solana/spl-token'
import { PublicKey, SystemProgram } from '@solana/web3.js'
import type { WalletContextState } from '@solana/wallet-adapter-react'
import { TREASURY_PROGRAM_ID } from './addresses'
import {
  treasuryTokenConfigPda,
  treasuryVaultAuthorityPda,
  getPlayerDepositPda
} from './helpers'
import { createTreasuryClient } from './programs'
import { getOrCreateAtaFrontend } from './account'
import { connection } from './connection'
import type { PlayerDepositAccountData } from './programs'
import { toBaseUnits } from './token-amount'

export async function getPlayerDepositSolana(
  player: PublicKey,
  tokenMint: PublicKey
): Promise<{ publicKey: PublicKey; account: PlayerDepositAccountData } | null> {
  try {
    const { treasuryProgram } = createTreasuryClient({
      publicKey: player,
      signTransaction: async tx => tx,
      signAllTransactions: async txs => txs
    } as WalletContextState)
    const playerDepositPda = getPlayerDepositPda(player, tokenMint)
    const depositAccount =
      await treasuryProgram.account.playerDeposit.fetch(playerDepositPda)
    return {
      publicKey: playerDepositPda,
      account: depositAccount as unknown as PlayerDepositAccountData
    }
  } catch {
    return null
  }
}

// eslint-disable-next-line max-statements
export async function depositSolana(
  wallet: WalletContextState,
  amount: number,
  tokenMint: PublicKey
): Promise<string> {
  if (!wallet.publicKey) throw new Error('Wallet not connected')

  const { treasuryProgram } = createTreasuryClient(wallet)
  const mintInfo = await getMint(connection, tokenMint)
  const amountInLamports = toBaseUnits(amount, mintInfo.decimals)

  const userTokenAccount = await getOrCreateAtaFrontend({
    wallet,
    owner: wallet.publicKey,
    mint: tokenMint,
    ownerIsPda: false
  })

  const tokenConfigPda = treasuryTokenConfigPda(TREASURY_PROGRAM_ID, tokenMint)
  let tokenConfig
  try {
    tokenConfig =
      await treasuryProgram.account.tokenConfig.fetch(tokenConfigPda)
  } catch (err) {
    const msg =
      (
        (err as Error)?.message?.includes('Account does not exist') ||
        (err as Error)?.message?.includes('has no data')
      ) ?
        `Treasury is not initialized for this token (mint: ${tokenMint.toBase58()}). Ensure the treasury token config exists on this network.`
      : ((err as Error)?.message ?? 'Failed to load token config')
    throw new Error(msg)
  }

  const playerDeposit = getPlayerDepositPda(wallet.publicKey, tokenMint)

  const accounts = {
    user: wallet.publicKey,
    mint: tokenMint,
    userTokenAccount,
    vaultTokenAccount: tokenConfig.vaultTokenAccount,
    playerDeposit,
    tokenConfig: tokenConfigPda,
    tokenProgram: TOKEN_PROGRAM_ID,
    systemProgram: SystemProgram.programId
  }

  return treasuryProgram.methods
    .deposit(amountInLamports)
    .accounts(accounts)
    .rpc({ commitment: 'confirmed' })
}

/**
 * Withdraws `amount` (a decimal string in tokens) from the player's Treasury
 * deposit; without `amount`, the whole deposit, dust included.
 */
// eslint-disable-next-line max-statements, complexity
export async function withdrawSolana(
  wallet: WalletContextState,
  tokenMint: PublicKey,
  amount?: string
): Promise<string> {
  if (!wallet.publicKey) throw new Error('Wallet not connected')

  const { treasuryProgram } = createTreasuryClient(wallet)
  const playerDepositAccount = await getPlayerDepositSolana(
    wallet.publicKey,
    tokenMint
  )
  if (!playerDepositAccount) throw new Error('No deposit found')

  const depositBalance = playerDepositAccount.account.balance
  if (depositBalance.isZero()) throw new Error('No funds to withdraw')

  const withdrawAmount =
    amount === undefined ? depositBalance : (
      toBaseUnits(amount, (await getMint(connection, tokenMint)).decimals)
    )
  if (withdrawAmount.isZero()) throw new Error('Enter an amount above zero')
  if (withdrawAmount.gt(depositBalance)) {
    throw new Error('The amount exceeds your treasury balance')
  }

  const userTokenAccount = await getOrCreateAtaFrontend({
    wallet,
    owner: wallet.publicKey,
    mint: tokenMint,
    ownerIsPda: false
  })

  const tokenConfigPda = treasuryTokenConfigPda(TREASURY_PROGRAM_ID, tokenMint)
  let tokenConfig
  try {
    tokenConfig =
      await treasuryProgram.account.tokenConfig.fetch(tokenConfigPda)
  } catch (err) {
    const msg =
      (
        (err as Error)?.message?.includes('Account does not exist') ||
        (err as Error)?.message?.includes('has no data')
      ) ?
        `Treasury is not initialized for this token (mint: ${tokenMint.toBase58()}). Ensure the treasury token config exists on this network.`
      : ((err as Error)?.message ?? 'Failed to load token config')
    throw new Error(msg)
  }

  const vaultAuthority = treasuryVaultAuthorityPda(
    TREASURY_PROGRAM_ID,
    tokenMint
  )
  const playerDeposit = getPlayerDepositPda(wallet.publicKey, tokenMint)

  const accounts = {
    user: wallet.publicKey,
    mint: tokenMint,
    userTokenAccount,
    vaultTokenAccount: tokenConfig.vaultTokenAccount,
    vaultAuthority,
    playerDeposit,
    tokenConfig: tokenConfigPda,
    tokenProgram: TOKEN_PROGRAM_ID
  }

  return treasuryProgram.methods
    .withdraw(withdrawAmount)
    .accounts(accounts)
    .rpc({ commitment: 'confirmed' })
}
